/**
 * Subtle smooth scrolling: eases mouse-wheel scrolling so it feels weighted
 * rather than stepped, without touching touch/trackpad momentum (which stays
 * fully native) or breaking in-page anchor links.
 *
 * Wraps Lenis (assets/vendor-lenis.js). Complements scroll-reveal.js: Lenis
 * drives the real scroll container's native scrollTop/scrollTo rather than a
 * transform-faked scroll, so the IntersectionObserver in scroll-reveal.js
 * keeps firing exactly as it would with native scrolling.
 *
 * This theme doesn't always scroll `window`: at >=990px ("squeeze" layout,
 * see base.css / scroll-container.js) `.page-wrapper` is the real scrolling
 * element while `html`/`body` are `overflow: hidden`. Below 990px the
 * document scrolls natively. We read the current scroll container from
 * scroll-container.js and recreate Lenis whenever that breakpoint crosses.
 */

import Lenis from '@theme/vendor-lenis';
import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

/** Tune the feel here. */
const CONFIG = {
  // Damping factor (frame-rate independent). Lower = more lag/smoothing, higher = snappier/closer to native.
  // Lenis's own default is 0.1.
  lerp: 0.11,
  // Multiplier applied to wheel input. 1 = native wheel speed.
  wheelMultiplier: 1,
  // Extra offset (px) applied when smoothly scrolling to an in-page anchor, e.g. to clear a sticky header.
  anchorOffset: 0,
};

/** @type {Lenis | undefined} */
let lenis;

if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  init();
}

function init() {
  createLenis();
  scrollContainerMediaQuery.addEventListener('change', () => {
    lenis?.destroy();
    createLenis();
  });
}

function createLenis() {
  const wrapper = getScrollContainer();

  lenis = new Lenis({
    wrapper,
    content: wrapper,
    lerp: CONFIG.lerp,
    wheelMultiplier: CONFIG.wheelMultiplier,
    anchors: CONFIG.anchorOffset ? { offset: -CONFIG.anchorOffset } : true,
    autoRaf: true,
    prevent: hasOwnScroll,
  });
}

/**
 * Lets Lenis know to leave wheel/touch input alone over elements that scroll
 * independently (carousels, mega-menu panels, filter lists, etc.), instead of
 * hijacking it for the page-level smoothing. Detected structurally so new
 * nested-scroll elements are covered automatically, with no markup changes.
 *
 * @param {Element} node
 * @returns {boolean}
 */
function hasOwnScroll(node) {
  if (!(node instanceof Element)) return false;

  const style = getComputedStyle(node);
  const scrollsX =
    (style.overflowX === 'auto' || style.overflowX === 'scroll') && node.scrollWidth > node.clientWidth;
  const scrollsY =
    (style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight;

  return scrollsX || scrollsY;
}
